import { useCallback, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { Badge, EmptyState, Page, Table, Text, type Column } from "@marekl/adminer/components";
import { useConfirm } from "@marekl/adminer/hooks";
import { formatDateTime, pluralizeCzech } from "@marekl/adminer/format";
import { AppTopbar } from "../../../shell/AppTopbar";
import { useAdminSeasons } from "../../../lib/queries/seasons";
import {
  useRevokeDocumentConsent,
  useSeasonDocumentConsents,
  useSeasonDocuments,
  type TeamDocumentConsent,
} from "../../../lib/queries/seasonDocuments";
import type { ID } from "../../../lib/id";
import { readsOnlyAsGlobalViewer, useMe } from "../../../auth/queries";

/**
 * Which teams agreed to one season document, any version, and the admin's
 * way to remove a consent recorded in error. Not sortable: newest version
 * first, then by team, is the order the question is asked in.
 */
export function SeasonDocumentConsentsPage() {
  const { data: me } = useMe();
  const readOnly = readsOnlyAsGlobalViewer(me, "members_admin");
  const { id: seasonId = "", documentId = "" } = useParams<{ id: ID; documentId: ID }>();
  const { data: seasons } = useAdminSeasons();
  const season = seasons?.find((s) => s.id === seasonId);
  const { data: docs } = useSeasonDocuments(seasonId);
  const doc = docs?.find((d) => d.id === documentId);
  const { data: rows = [], isLoading, error, refetch } = useSeasonDocumentConsents(documentId);
  const confirm = useConfirm();
  // Destructured: mutateAsync is stable, so the memoised columns are not
  // rebuilt on every state change of the mutation.
  const { mutateAsync: revokeConsent } = useRevokeDocumentConsent();

  const askRevoke = useCallback(
    (c: TeamDocumentConsent) =>
      confirm({
        title: "Odebrat souhlas",
        description: (
          <>
            Souhlas týmu <strong>{c.team_name}</strong> s verzí {c.version} se smaže. Pokud jde o
            platnou verzi, tým bude o souhlas znovu požádán. Odebrání se zapíše do audit logu.
          </>
        ),
        confirmLabel: "Odebrat souhlas",
        variant: "danger",
        onConfirm: async () => {
          await revokeConsent(c.id);
        },
      }),
    [confirm, revokeConsent],
  );

  const columns = useMemo<Column<TeamDocumentConsent>[]>(
    () => [
      {
        key: "team",
        header: "Tým",
        render: (c) => <Link to={`/teams/${c.team_id}`}>{c.team_name}</Link>,
      },
      {
        key: "version",
        header: "Verze",
        render: (c) => (
          <>
            {c.version}{" "}
            {c.current ? <Badge variant="ok">Platná</Badge> : <Badge variant="muted">Starší</Badge>}
          </>
        ),
      },
      {
        key: "agreed",
        header: "Odsouhlaseno",
        render: (c) => (
          <>
            <div>{formatDateTime(c.agreed_at)}</div>
            <Text variant="muted">
              {c.user_name || "—"}
              {c.impersonator_name ? ` (v zastoupení: ${c.impersonator_name})` : ""}
            </Text>
          </>
        ),
      },
      ...(readOnly
        ? []
        : [
            {
              key: "actions",
              header: "",
              render: (c: TeamDocumentConsent) => (
                <Table.Actions>
                  <Table.Action appearance="button" variant="danger" onClick={() => askRevoke(c)}>
                    Odebrat souhlas
                  </Table.Action>
                </Table.Actions>
              ),
            },
          ]),
    ],
    [askRevoke, readOnly],
  );

  const code = season?.code ?? "";
  const current = rows.filter((c) => c.current).length;
  return (
    <>
      <AppTopbar
        crumbs={[
          { label: "Sezóny", href: "/admin/sezony" },
          { label: `Dokumenty ${code}`, href: `/admin/sezony/${seasonId}/dokumenty` },
          { label: doc?.name ?? "Souhlasy" },
        ]}
      />
      <Page>
        <Page.Header
          title={doc ? `Souhlasy: ${doc.name}` : "Souhlasy"}
          subtitle={`s platnou verzí souhlasí ${current} ${pluralizeCzech(current, ["tým", "týmy", "týmů"])}`}
        />
        <Page.Content>
          <Table<TeamDocumentConsent>
            framed
            rowKey={(c) => c.id}
            columns={columns}
            rows={rows}
            isLoading={isLoading}
            error={error}
            onRetry={() => void refetch()}
            empty={
              <EmptyState
                title="Žádné souhlasy"
                description="S tímto dokumentem zatím žádný tým nesouhlasil."
              />
            }
          />
        </Page.Content>
      </Page>
    </>
  );
}
